import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Box, Text, Button, Grid, GridItem, VStack, Heading, useToast,
  Table, Thead, Tbody, Tr, Th, Td, Badge, Progress, Icon, HStack, Select,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton
} from "@chakra-ui/react";
import { AttachmentIcon, CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';

import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';

function BulkUpload() {
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [uploads, setUploads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Options state
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedCaseType, setSelectedCaseType] = useState('Automatic');
  const [selectedPayer, setSelectedPayer] = useState('');
  const [selectedState, setSelectedState] = useState('');

  // Report Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUploadForReport, setSelectedUploadForReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  const caseTypeOptions = [
    'Automatic',
    'Clinical',
    'E/M',
    'Patient Query',
    'Nursing Facility',
    'Gastro',
    'IONM Modern',
  ];
  const payerOptions = ['BCBS', 'UHC', 'Aetna', 'CIGNA', 'Medicare'];
  const stateOptions = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  const fetchUploads = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const response = await callApi('/api/v1/bulk_uploads', accessToken);
      const data = await response.json();
      if (response.ok) {
        setUploads(data.uploads || []);
      } else {
        throw new Error(data.error || 'Failed to fetch uploads');
      }
    } catch (error) {
      toast({
        title: 'Error fetching uploads',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClients = async () => {
    if (!accessToken) return;
    setClientsLoading(true);
    try {
      const response = await callApi('/clients', accessToken);
      const data = await response.json();
      if (response.ok && data.clients) {
        const activeClients = data.clients.filter(client => client.active);
        setAvailableClients(activeClients);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  useEffect(() => {
    fetchUploads();
    fetchClients();
  }, [accessToken]);

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    const client = clientId ? availableClients.find(c => c.id === parseInt(clientId)) : null;
    setSelectedClient(client);
  };

  const fetchReports = async (uploadId) => {
    if (!accessToken) return;
    setReportsLoading(true);
    setReports([]);
    try {
      const response = await callApi(`/api/v1/bulk_uploads/${uploadId}/reports`, accessToken);
      const data = await response.json();
      if (response.ok) {
        setReports(data.reports || []);
      } else {
        throw new Error(data.error || 'Failed to fetch reports');
      }
    } catch (error) {
      toast({
        title: 'Error fetching reports',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setReportsLoading(false);
    }
  };

  const handleRowClick = (upload) => {
    if (upload.status === 'pending_upload') return;
    
    setSelectedUploadForReport(upload);
    setIsModalOpen(true);
    fetchReports(upload.id);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUploadForReport(null);
    setReports([]);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/zip' || file.name.endsWith('.zip')) {
        setSelectedFile(file);
      } else {
        toast({
          title: 'Invalid File Type',
          description: 'Please select a .zip file.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({ title: 'No file selected', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Construct the options object
      const options = {};
      if (selectedClient) {
        options.client_id = selectedClient.id;
      }
      if (selectedCaseType && selectedCaseType !== 'Automatic') {
        options.case_type = selectedCaseType;
      }
      if (selectedPayer) {
        options.payer = selectedPayer;
      }
      if (selectedState) {
        options.state = selectedState;
      }

      // Step 1: Get pre-signed URL from our backend
      const presignResponse = await callApi('/api/v1/bulk_uploads/generate_presigned_url', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: selectedFile.name,
          file_type: selectedFile.type,
          file_size: selectedFile.size,
        }),
      });

      const presignData = await presignResponse.json();
      if (!presignResponse.ok) {
        throw new Error(presignData.error || 'Could not get upload URL.');
      }

      // Step 2: Upload the file directly to S3 using the pre-signed data
      const { url, fields } = presignData.presigned_data;
      const s3_key = presignData.s3_key; // Get the s3_key for the confirmation step
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', selectedFile);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
        }
      };

      xhr.onload = async () => {
        if (xhr.status === 204) {
          // Step 3: Confirm the upload with our backend
          try {
            const confirmResponse = await callApi('/api/v1/bulk_uploads', accessToken, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                s3_key: s3_key,
                filename: selectedFile.name,
                file_type: selectedFile.type,
                file_size: selectedFile.size,
                options: options,
              }),
            });

            const confirmData = await confirmResponse.json();
            if (!confirmResponse.ok) {
              throw new Error(confirmData.error || 'Failed to confirm upload.');
            }

            toast({
              title: 'Upload Successful',
              description: `${selectedFile.name} has been uploaded and verified.`,
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            fetchUploads(); // Refresh the list of uploads

          } catch (confirmError) {
            toast({
              title: 'Upload Confirmation Failed',
              description: `The file was uploaded to S3, but confirming with our server failed. Please contact support. Details: ${confirmError.message}`,
              status: 'error',
              duration: 9000,
              isClosable: true,
            });
          }
        } else {
          console.error('S3 Upload Error:', xhr.responseText);
          toast({
            title: 'Upload Failed',
            description: 'The file could not be uploaded to S3. Check console for details.',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
        setIsUploading(false);
        setSelectedFile(null);
        setUploadProgress(0);
        fileInputRef.current.value = null;
      };

      xhr.onerror = () => {
        toast({
          title: 'Upload Error',
          description: 'A network error occurred during the upload.',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
        setIsUploading(false);
        setSelectedFile(null);
        setUploadProgress(0);
        fileInputRef.current.value = null;
      };

      xhr.send(formData);

    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_upload':
        return <Badge colorScheme="yellow">Pending</Badge>;
      case 'uploaded':
        return <Badge colorScheme="green">Uploaded</Badge>;
      case 'processing':
        return <Badge colorScheme="blue">Processing</Badge>;
      case 'processed':
        return <Badge colorScheme="purple">Processed</Badge>;
      case 'error':
        return <Badge colorScheme="red">Error</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <Box fontSize="xl">
      <Grid
        templateAreas={`"header" "main"`}
        gridTemplateRows={'60px 1fr'}
        h='100vh'
      >
        <HeaderMenus />
        <GridItem bg='white' padding={8} area={'main'}>
          <VStack spacing={8} align="stretch">
            <Heading size="lg">Bulk File Upload</Heading>
            
            {/* Upload Section */}
            <Box p={6} borderWidth="1px" borderRadius="md" borderColor="gray.200">
              <VStack spacing={4} align="stretch">
                <Heading size="md">Processing Options</Heading>
                <Text fontSize="sm">Select options to apply to all cases in this upload (optional).</Text>

                {availableClients.length > 0 && (
                  <HStack spacing={4} align="center" w="100%">
                    <Text fontSize="sm" color="gray.600" w="60px">Client:</Text>
                    <Select 
                      placeholder="Select client"
                      value={selectedClient?.id || ''}
                      onChange={handleClientChange}
                      size="sm"
                      variant="filled"
                      isDisabled={clientsLoading}
                    >
                      {availableClients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.name} ({client.description})
                        </option>
                      ))}
                    </Select>
                  </HStack>
                )}

                <HStack spacing={4} align="center" w="100%">
                  <Text fontSize="sm" color="gray.600" w="60px">Case Type:</Text>
                  <Select
                    value={selectedCaseType}
                    onChange={(e) => setSelectedCaseType(e.target.value)}
                    size="sm"
                    variant="filled"
                  >
                    {caseTypeOptions.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Select>
                </HStack>

                <HStack spacing={4} align="center" w="100%">
                  <Text fontSize="sm" color="gray.600" w="60px">Payer:</Text>
                  <Select 
                    placeholder="Select payer"
                    value={selectedPayer}
                    onChange={(e) => setSelectedPayer(e.target.value)}
                    size="sm"
                    variant="filled"
                  >
                    {payerOptions.map(payer => (
                      <option key={payer} value={payer}>{payer}</option>
                    ))}
                  </Select>
                </HStack>

                <HStack spacing={4} align="center" w="100%">
                  <Text fontSize="sm" color="gray.600" w="60px">State:</Text>
                  <Select 
                    placeholder="Select state"
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    size="sm"
                    variant="filled"
                  >
                    {stateOptions.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </Select>
                </HStack>

                <Heading size="md" mt={4}>New Upload</Heading>
                <Text>Select a .zip file to upload directly to our secure storage.</Text>
                
                <input
                  type="file"
                  accept=".zip,application/zip"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                />
                
                <HStack>
                  <Button
                    leftIcon={<AttachmentIcon />}
                    onClick={() => fileInputRef.current.click()}
                    isDisabled={isUploading}
                  >
                    Choose File
                  </Button>
                  {selectedFile && <Text fontSize="md">{selectedFile.name}</Text>}
                </HStack>

                {isUploading && <Progress value={uploadProgress} size="lg" colorScheme="green" hasStripe isAnimated />}

                <Button
                  colorScheme="green"
                  onClick={handleUpload}
                  isDisabled={!selectedFile || isUploading}
                  isLoading={isUploading}
                  loadingText={`Uploading... ${uploadProgress}%`}
                >
                  Upload File
                </Button>
              </VStack>
            </Box>

            {/* History Section */}
            <Box>
              <Heading size="md" mb={4}>Upload History</Heading>
              <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                <Table variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Filename</Th>
                      <Th>Status</Th>
                      <Th>Size</Th>
                      <Th>Upload Date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {isLoading ? (
                      <Tr><Td colSpan="4" textAlign="center">Loading...</Td></Tr>
                    ) : uploads.length > 0 ? (
                      uploads.map(upload => (
                        <Tr 
                          key={upload.id} 
                          onClick={() => handleRowClick(upload)}
                          cursor={upload.status === 'pending_upload' ? 'default' : 'pointer'}
                          _hover={upload.status === 'pending_upload' ? {} : { bg: "gray.50" }}
                        >
                          <Td fontWeight="medium">{upload.original_filename}</Td>
                          <Td>{getStatusBadge(upload.status)}</Td>
                          <Td>{formatFileSize(upload.file_size)}</Td>
                          <Td>{new Date(upload.created_at).toLocaleString()}</Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr><Td colSpan="4" textAlign="center">No uploads found.</Td></Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </VStack>
        </GridItem>
      </Grid>

      {/* Report Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Processing Report: {selectedUploadForReport?.original_filename}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {reportsLoading ? (
              <Text>Loading reports...</Text>
            ) : reports.length > 0 ? (
              <VStack spacing={8} align="stretch">
                {reports.map((report, index) => (
                  <Box key={report.id} borderWidth="1px" borderRadius="md" p={4}>
                    <Heading size="sm" mb={2}>
                      Run #{reports.length - index} - {new Date(report.created_at).toLocaleString()}
                    </Heading>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                      Source: {report.report_data.source_file} | Processed: {new Date(report.report_data.processed_at).toLocaleString()}
                    </Text>
                    
                    <Table size="sm" variant="striped">
                      <Thead>
                        <Tr>
                          <Th>External Case ID</Th>
                          <Th>Case UUID</Th>
                          <Th>Outcome</Th>
                          <Th>Error Message</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {report.report_data.items && report.report_data.items.map((item, idx) => (
                          <Tr key={idx}>
                            <Td>{item.external_case_id}</Td>
                            <Td>{item.case_uuid || '-'}</Td>
                            <Td>
                              <Badge colorScheme={item.outcome === 'success' ? 'green' : 'red'}>
                                {item.outcome}
                              </Badge>
                            </Td>
                            <Td color="red.500" fontSize="xs" maxW="300px" isTruncated title={item.error_message}>
                              {item.error_message || '-'}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Box textAlign="center" py={10}>
                <Text fontSize="lg">No reports found for this upload.</Text>
                <Text color="gray.500">The file might still be processing or encountered an error before report generation.</Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={closeModal}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default BulkUpload;